import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  ShareOutlined as ShareIcon,
  AddBoxOutlined as AddHomeIcon,
  MoreVert as MoreVertIcon,
  MoreHoriz as MoreHorizIcon,
  GetApp as GetAppIcon,
  OpenInBrowser as OpenInBrowserIcon,
  CheckCircleOutline as CheckIcon,
  Apps as AppsIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const GALDRIS_GREEN = '#115740';

function Step({ number, title, detail, icon }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: GALDRIS_GREEN,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '0.95rem',
          flexShrink: 0,
          mt: 0.25
        }}
      >
        {number}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {icon}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {detail}
        </Typography>
      </Box>
    </Box>
  );
}

function GuideShell({ title, browserLabel, children, onClose }) {
  return (
    <>
      <DialogTitle sx={{ pr: 6, fontWeight: 700, pb: 1 }}>{title}</DialogTitle>
      <DialogContent>
        {browserLabel && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You are using <strong>{browserLabel}</strong>. Follow every step below in order.
          </Alert>
        )}
        {children}
        <Alert severity="success" sx={{ mt: 1 }}>
          After installing, open Galldris from your home screen, taskbar, or Start menu - like any other app.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none', bgcolor: GALDRIS_GREEN }}>
          Got it
        </Button>
      </DialogActions>
    </>
  );
}

function InAppBrowserGuide({ onClose }) {
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied — paste it in Chrome, Edge, or Safari');
    } catch {
      toast.error('Could not copy link. Type the address from your browser bar.');
    }
  };

  return (
    <GuideShell title="Open in Chrome, Edge, or Safari first" onClose={onClose}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You opened Galldris inside another app (e.g. Facebook or WhatsApp). Install only works in a real browser.
      </Typography>
      <Step
        number={1}
        icon={<OpenInBrowserIcon fontSize="small" color="primary" />}
        title="Tap the menu in this app"
        detail='Look for three dots (⋯) and choose "Open in browser", "Open in Chrome", or "Open in Safari".'
      />
      <Step
        number={2}
        icon={<ShareIcon fontSize="small" color="primary" />}
        title="Or copy this link"
        detail="Paste the link into Chrome, Microsoft Edge, or Safari, then tap Install Galldris again on the login page."
      />
      <Button variant="outlined" fullWidth onClick={handleCopyLink} sx={{ mt: 0.5, textTransform: 'none', fontWeight: 600 }}>
        Copy link to clipboard
      </Button>
    </GuideShell>
  );
}

function IOSGuide({ onClose }) {
  return (
    <GuideShell title="Add Galldris to your home screen" browserLabel="Safari on iPhone or iPad" onClose={onClose}>
      <Step
        number={1}
        icon={<ShareIcon fontSize="small" color="primary" />}
        title="Tap Share"
        detail="At the bottom of Safari — square icon with an arrow pointing up."
      />
      <Step
        number={2}
        icon={<AddHomeIcon fontSize="small" color="primary" />}
        title='Tap "Add to Home Screen"'
        detail="Scroll down in the menu if you do not see it straight away."
      />
      <Step
        number={3}
        icon={<CheckIcon fontSize="small" color="primary" />}
        title='Tap "Add" (top right)'
        detail="Galldris appears on your home screen. Tap the icon to open it."
      />
    </GuideShell>
  );
}

function AndroidGuide({ onClose, browser }) {
  const isEdge = browser?.isEdge;
  const browserLabel = isEdge ? 'Microsoft Edge on Android' : 'Google Chrome on Android';

  return (
    <GuideShell title="Install Galldris on this phone" browserLabel={browserLabel} onClose={onClose}>
      <Step
        number={1}
        icon={<MoreVertIcon fontSize="small" color="primary" />}
        title="Tap the three dots"
        detail={`Top-right corner of ${isEdge ? 'Edge' : 'Chrome'}.`}
      />
      <Step
        number={2}
        icon={<GetAppIcon fontSize="small" color="primary" />}
        title={isEdge ? 'Tap "Add to phone" or "Install"' : 'Tap "Install app" or "Add to Home screen"'}
        detail="The exact words can vary slightly by phone."
      />
      <Step
        number={3}
        icon={<CheckIcon fontSize="small" color="primary" />}
        title='Tap "Install" or "Add"'
        detail="Galldris opens from your home screen next time — no need to find it in the browser."
      />
    </GuideShell>
  );
}

function EdgeDesktopGuide({ onClose }) {
  return (
    <GuideShell title="Install Galldris Core on this computer" browserLabel="Microsoft Edge" onClose={onClose}>
      <Step
        number={1}
        icon={<MoreHorizIcon fontSize="small" color="primary" />}
        title="Click the three dots (⋯)"
        detail="Top-right corner of the Edge window — next to your profile picture."
      />
      <Step
        number={2}
        icon={<ShareIcon fontSize="small" color="primary" />}
        title='Click "Cast, save and share"'
        detail="It is in the menu that opens — not at the very top."
      />
      <Step
        number={3}
        icon={<GetAppIcon fontSize="small" color="primary" />}
        title='Click "Install page as app..."'
        detail='Under the "Save" section. This adds Galldris as an app on your PC.'
      />
      <Step
        number={4}
        icon={<CheckIcon fontSize="small" color="primary" />}
        title='Click "Install" in the popup'
        detail="Edge may ask to pin Galldris to your taskbar — you can tick that box too."
      />
    </GuideShell>
  );
}

function ChromeDesktopGuide({ onClose }) {
  return (
    <GuideShell title="Install Galldris Core on this computer" browserLabel="Google Chrome" onClose={onClose}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Try the <strong>easy way</strong> first. If you do not see the install icon, use the menu steps below.
      </Typography>
      <Step
        number={1}
        icon={<GetAppIcon fontSize="small" color="primary" />}
        title="Look at the address bar (top)"
        detail='On the right side of the web address, click the install icon (small monitor or ⊕ with "Install"). Then click Install.'
      />
      <Typography variant="body2" sx={{ fontWeight: 700, my: 1.5 }}>
        If you do not see that icon, do this instead:
      </Typography>
      <Step
        number={2}
        icon={<MoreVertIcon fontSize="small" color="primary" />}
        title="Click the three dots (⋮)"
        detail="Top-right corner of Chrome."
      />
      <Step
        number={3}
        icon={<ShareIcon fontSize="small" color="primary" />}
        title='Click "Cast, save, and share"'
        detail="In the menu that opens."
      />
      <Step
        number={4}
        icon={<AppsIcon fontSize="small" color="primary" />}
        title='Click "Install page as app..."'
        detail='Then click "Install" in the popup.'
      />
    </GuideShell>
  );
}

function GenericDesktopGuide({ onClose, browser }) {
  return (
    <GuideShell
      title="Install Galldris Core on this computer"
      browserLabel={browser?.name || 'your browser'}
      onClose={onClose}
    >
      <Step
        number={1}
        icon={<GetAppIcon fontSize="small" color="primary" />}
        title="Look for an install icon in the address bar"
        detail="Often a monitor, plus, or download icon on the right of the web address."
      />
      <Step
        number={2}
        icon={<MoreVertIcon fontSize="small" color="primary" />}
        title="Or open the browser menu (three dots)"
        detail='Look for "Install", "Install app", or "Install page as app".'
      />
      <Step
        number={3}
        icon={<CheckIcon fontSize="small" color="primary" />}
        title='Click "Install" when asked'
        detail="Use Google Chrome or Microsoft Edge for the best experience."
      />
    </GuideShell>
  );
}

function DesktopGuide({ onClose, browser }) {
  if (browser?.isEdge) return <EdgeDesktopGuide onClose={onClose} />;
  if (browser?.isChrome) return <ChromeDesktopGuide onClose={onClose} />;
  return <GenericDesktopGuide onClose={onClose} browser={browser} />;
}

export default function PWAInstallGuideDialog({ open, onClose, platform }) {
  const browser = platform?.browser;

  const content = platform?.isInAppBrowser ? (
    <InAppBrowserGuide onClose={onClose} />
  ) : platform?.isIOS ? (
    <IOSGuide onClose={onClose} />
  ) : platform?.isAndroid ? (
    <AndroidGuide onClose={onClose} browser={browser} />
  ) : (
    <DesktopGuide onClose={onClose} browser={browser} />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
      >
        <CloseIcon />
      </IconButton>
      {content}
    </Dialog>
  );
}
